import { z } from "zod";

import { SaleTypeForTerm, VoucherLimitType } from "@/lib/gql/__generated__/graphql";
import { VoucherInputSchema } from "@/lib/zod/__generated__/validation-schema";
import { nonNegativeNumber } from "@/lib/zod/custom-validation-schema";

import {
	chillnnDateZodSchema,
	priceObjectZodSchema,
	s3ObjectZodSchema,
} from "@/components/features/_shared/validation-schema/validation-schema";

export const voucherInputSchema = VoucherInputSchema.extend({
	name: z.string().min(1).max(100),
	description: z.string().min(1).max(1000),

	mainKeyVisual: s3ObjectZodSchema,

	sellingPrice: priceObjectZodSchema,
	voucherPrice: priceObjectZodSchema,

	saleStart: chillnnDateZodSchema.nullish().or(z.literal("")),
	saleEnd: chillnnDateZodSchema.nullish().or(z.literal("")),

	cinStart: chillnnDateZodSchema.nullish().or(z.literal("")),
	cinEnd: chillnnDateZodSchema.nullish().or(z.literal("")),

	intervalPrepDates: z
		.number()
		.int()
		.max(1825)
		.nullish()
		.refine((value) => nonNegativeNumber({ value, threshold: 0 }), {
			message: "利用可能日は0日以降で入力してください",
		}),
	maxStockNum: z
		.number()
		.int()
		.nullish()
		.refine((value) => nonNegativeNumber({ value, threshold: 1 }), {
			message: "合計発行枚数の上限を入力する場合は1以上で入力してください",
		}),
})
	// 公開期間
	.refine(
		({ saleStart, saleEnd, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				if (!!saleStart && !!saleEnd) {
					return saleStart <= saleEnd;
				}

				// 宿泊券は期間限定販売の場合、公開期間しか設定が存在しないので開始日・終了日の両方を必須にしている
				return false;
			}

			return true;
		},
		{
			message: "公開する期間を設定してください",
			path: ["saleStart"],
		},
	)
	// 購入してから利用可能になるまでの日数
	.refine(
		({ intervalPrepDates, voucherLimitType }) => {
			if (voucherLimitType === VoucherLimitType.Interval) {
				if (typeof intervalPrepDates !== "number") {
					return false;
				}

				return intervalPrepDates >= 0;
			}

			return true;
		},
		{
			message: "購入してから利用可能になるまでの日数を設定してください",
			path: ["intervalPrepDates"],
		},
	)
	// チェックイン可能な期間
	.refine(
		({ cinStart, cinEnd, voucherLimitType }) => {
			if (voucherLimitType === VoucherLimitType.StayDate) {
				if (!!cinStart && !!cinEnd) {
					return cinStart < cinEnd;
				}

				// サーバー側で cinStart / cinEnd が両方必須のため、片側だけの保存はエラーになる
				return false;
			}

			return true;
		},
		{
			message: "チェックイン可能な期間を設定してください",
			path: ["cinStart"],
		},
	);

export type VoucherInputSchema = z.infer<typeof voucherInputSchema>;
